import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataEndChannel } from "../../shared/interfaces/data-end-channel.interface";
import { DataEndChannelTypeEnum, getDataEndChannelTypeString, getDataEndChannelTypeIcon } from "../../shared/enums/data-end-channel-type.enum";
import { ModalService } from "../../../shared/modal/shared/modal.service";
import { DataEndChannelService } from "../../shared/services/data-end-channel.service";
import { AlertService } from "../../../shared/alert/shared/alert.service";
import { AlertTypes } from "../../../shared/alert/shared/alert-types";
import { MatDialog } from "@angular/material/dialog";
import { DataEndChannelEditModalComponent } from "../data-end-channel-edit-modal/data-end-channel-edit-modal.component";
import { DataEndChannelError } from "../../shared/interfaces/data-end-channel-error.interface";
import { DataEndChannelSettingsDialogComponent } from '../shared/data-end-channel-settings-dialog/data-end-channel-settings-dialog.component';
import { getLocalBusTypeString } from './local-bus-type.enum';
import { FallbackImageSettingsComponent } from '../shared/fallback-image-settings/fallback-image-settings.component';
import { Fallbacksettings } from '../shared/fallbacksettings';
import { DeviceService } from 'src/app/device/shared/services/device.service';
import { SensorSettingsComponent } from '../shared/sensor-settings/sensor-settings.component';
import { FallbackFlasherSettingsComponent } from '../shared/fallback-flasher-settings/fallback-flasher-settings.component';
import { ErrorThresholdService } from 'src/app/error-engine/shared/service/error-threshold.service';
import { CreateEditErrorTresholdComponent } from './shared/create-edit-error-treshold/create-edit-error-treshold.component';
import { PixelFehlerDialogComponent } from './shared/pixel-fehler-dialog/pixel-fehler-dialog.component';
import { ErrorThreshold } from 'src/app/error-engine/shared/interfaces/error-threshold.interface';
import { ErrorThresholdParameters } from 'src/app/error-engine/shared/interfaces/error-threshold-parameters.interface';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { SchemaDeviceInfo, SchemaRadarInfo, SchemaRouteStationInfo } from '../../project-schema/shared/interfaces/schema-route-station-info.interface';
import { DeviceInfoDialogComponent, DeviceInfoDialogData } from './shared/device-info-dialog/device-info-dialog.component';
import { getChannelDeviceInfo } from '../../shared/functions/schema-device-info';
import { DigitalInputEngineParameterSettingsDialogComponent } from '../shared/digital-input-engine-parameter-settings-dialog/digital-input-engine-parameter-settings-dialog.component';

@Component({
  selector: 'avs-data-end-channel-table',
  templateUrl: './data-end-channel-table.component.html',
  styleUrls: ['./data-end-channel-table.component.css']
})
export class DataEndChannelTableComponent implements OnChanges {
  @Input() dataEndChannels: DataEndChannel[] = [];
  @Input() dataEndChannelErrors: DataEndChannelError[] = [];
  @Input() routeStationGuid?: string;
  @Input() projectId?: number;
  @Input() channelAdded: boolean = false;
  @Input() samId?: number;
  @Output() dataEndChannelErrorsRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() dataEndChannelRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();
  /** Radar-Sicht der Station (fuer Geraetetyp-Erkennung der Radarkanaele) */
  @Input() radars: SchemaRadarInfo[] = [];
  /** Schema-Sicht der Station (liefert die Geraeteinformationen je Kanal) */
  @Input() schemaInfo?: SchemaRouteStationInfo;
  public detecedChanges: boolean = false;
  /** Geraeteinformationen je Kanal-Id, nur fuer Kanaele mit bekanntem Geraet */
  public deviceInfoByChannelId = new Map<number, SchemaDeviceInfo>();

  public isSurveillanceForAllDeActive: boolean = false;
  public isLoggingForAllDeActive: boolean = false;
  public loggingActive: boolean = false;

  protected readonly getDataEndChannelTypeString = getDataEndChannelTypeString;
  protected readonly getDataEndChannelTypeIcon = getDataEndChannelTypeIcon;
  protected readonly getLocalBusTypeString = getLocalBusTypeString;
  constructor(private dataEndChannelService: DataEndChannelService,
    private alertService: AlertService,
    private modalService: ModalService,
    private dialog: MatDialog,
    private deviceService: DeviceService,
    private errorTresholdService: ErrorThresholdService) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['schemaInfo'] || changes['dataEndChannels']) {
      this.deviceInfoByChannelId = new Map(
        this.dataEndChannels
          .map(channel => [channel.id, getChannelDeviceInfo(channel, this.schemaInfo)] as const)
          .filter((entry): entry is readonly [number, SchemaDeviceInfo] => entry[1] !== null)
      );
    }
  }

  /** Oeffnet die Geraeteinformationen des Kanals; ohne bekanntes Geraet passiert nichts (Tooltip weist darauf hin). */
  public showDeviceInfo(dataEndChannel: DataEndChannel): void {
    const device = this.deviceInfoByChannelId.get(dataEndChannel.id);
    if (!device) { return; }
    const data: DeviceInfoDialogData = {
      title: `${getDataEndChannelTypeString(dataEndChannel.dataEndChannelType)} – DE ${dataEndChannel.de}`,
      device
    };
    this.dialog.open(DeviceInfoDialogComponent, { data });
  }

  public onEditBtnClicked(dataEndChannel: DataEndChannel): void {
    // The dialog edits its data in place; a copy keeps the listed channel (and everything bound to it,
    // e.g. the fuel cell settings) unchanged until the dialog has saved and the list is reloaded.
    const dialogRef = this.dialog.open(DataEndChannelEditModalComponent, { data: { ...dataEndChannel } });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) { this.dataEndChannelRefresh.emit(true); this.detecedChanges = true; }
      else return;
    });
  }
  public onDeleteBtnClicked(dataEndChannel: DataEndChannel): void {
    this.modalService.openDialog('Datenendkanal löschen?', 'Nein', 'Ja').subscribe(() => {
      this.dataEndChannelService.deleteDataEndChannel(dataEndChannel.id).subscribe(() => {
        this.alertService.alert('Datenendkanal wurde erfolgreich entfernt', AlertTypes.info);
        this.detecedChanges = true;
        this.dataEndChannelRefresh.emit(true);
      });
    });
  }

  public onRemoveTimerAssignment(deKanalId: number): void {
    this.modalService.openDialog('Zeitschaltuhr Zuweisung entfernen?', 'Nein', 'Ja').subscribe(() => {
      this.dataEndChannelService.deleteDataEndChannelTimerAssignment(deKanalId).subscribe(() => {
        this.alertService.alert('Zeitschaltuhr Zuweisung wurde erfolgreich entfernt', AlertTypes.info);
        this.dataEndChannelRefresh.emit(true);
      });
    });
  }

  public onResetEvaClicked(): void {
    if (!this.routeStationGuid) { return; }
    this.deviceService.resetDevice(this.routeStationGuid, 0).subscribe(() => {
      this.alertService.alert("Eva Reset wurde ausgeführt", AlertTypes.info);
    });
  }

  public showFGSettings(dataEndChannel: DataEndChannel): void {
    let data;
    let fg: number = 0;
    let type = getDataEndChannelTypeString(dataEndChannel.dataEndChannelType);
    if (dataEndChannel.fG01_Settings.length > 2) { data = dataEndChannel.fG01_Settings; fg = 1; type = type }
    if (dataEndChannel.fG04_Settings.length > 2) { data = dataEndChannel.fG04_Settings; fg = 4; type = type }
    if (dataEndChannel.fG06_Settings.length > 2) { data = dataEndChannel.fG06_Settings; fg = 6; type = type }
    const dialogRef = this.dialog.open(DataEndChannelSettingsDialogComponent, { data: { data: data, fg: fg, guid: this.routeStationGuid, type: type, projectId: this.projectId } })
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let engineParameter = '{"EngineParameter": ' + JSON.stringify(result.settings) + '}';
        if (fg === 1) {
          dataEndChannel.fG01_Settings = engineParameter;
        }
        if (fg === 4) {
          dataEndChannel.fG04_Settings = engineParameter;
        }
        if (fg === 6) {
          dataEndChannel.fG06_Settings = engineParameter;
        }
        this.dataEndChannelService.editDataEndChannel(dataEndChannel.id, dataEndChannel).subscribe((x) => { this.alertService.alert('Einstellungen wurden erfolgreich gespeichert', AlertTypes.info); this.dataEndChannelRefresh.emit(true); });
      }
    })
  }

  public showDigitalInputSettings(dataEndChannel: DataEndChannel): void {
    let data;
    let fg: number = 0;
    let type = getDataEndChannelTypeString(dataEndChannel.dataEndChannelType);

    if (dataEndChannel.fG06_Settings /* .length > 2 */) { data = dataEndChannel.fG06_Settings; fg = 6; type = type }
    /* testweise einmal anlegen?
    und dann prüfen ob auslesen und parsen funktioniert?
    */

    const dialogRef = this.dialog.open(DigitalInputEngineParameterSettingsDialogComponent, { data: { data: data, fg: fg, guid: this.routeStationGuid, type: type, projectId: this.projectId } })
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let engineParameter = '{"EngineParameter": ' + JSON.stringify(result.settings) + '}';
        if (fg === 1) {
          dataEndChannel.fG01_Settings = engineParameter;
        }
        if (fg === 4) {
          dataEndChannel.fG04_Settings = engineParameter;
        }
        if (fg === 6) {
          dataEndChannel.fG06_Settings = engineParameter;
        }
        this.dataEndChannelService.editDataEndChannel(dataEndChannel.id, dataEndChannel).subscribe((x) => { this.alertService.alert('Einstellungen wurden erfolgreich gespeichert', AlertTypes.info); this.dataEndChannelRefresh.emit(true);});
      }
    })
  }

  public createFallbackSettings(dataEndChannel: DataEndChannel): void {
    const dialogRef = this.dialog.open(FallbackImageSettingsComponent, { data: { data: dataEndChannel, projectId: this.projectId } });
    dialogRef.afterClosed().subscribe((result: Fallbacksettings) => {
      if (!result) { return; }
      this.dataEndChannelService.setFallbackSettings(dataEndChannel.id, { anzeigeprinzip: 3, stellcode: result.stellcode, indicator: result.indicator, flashingTime: 0, delay: result.delay }).subscribe((dataEndChannelDto) => {
        this.dataEndChannels = this.dataEndChannels.map(x => x.id === dataEndChannelDto.id ? dataEndChannelDto : x);
        this.alertService.alert('Fallback Einstellungen wurden erfolgreich gespeichert', AlertTypes.info);
      });
    });
  }

  public checkPixelerror(dataEndChannel: DataEndChannel): void {
    this.dialog.open(PixelFehlerDialogComponent, { data: { guid: this.routeStationGuid, dataEndChannel: dataEndChannel } });
  }

  public editSensorSettings(dataEndChannel: DataEndChannel): void {
    const dialogRef = this.dialog.open(SensorSettingsComponent, { data: { data: dataEndChannel, projectId: this.projectId } });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) { return; }
      this.dataEndChannelService.setServerParameter(dataEndChannel.id, result.height, result.offset).subscribe((dataEndChannelDto) => {
        this.dataEndChannels = this.dataEndChannels.map(x => x.id === dataEndChannelDto.id ? dataEndChannelDto : x);
        this.deviceService.setAdecHeightOffset(this.samId!, dataEndChannel.de, result.height, result.offset).subscribe(() => {
          this.alertService.alert('Sensorparameter wurden erfolgreich auf das Gerät übertragen', AlertTypes.info);
        });
      });
    });
  }

  public createFallbackFlasherSettings(dataEndChannel: DataEndChannel): void {
    const dialogRef = this.dialog.open(FallbackFlasherSettingsComponent, { data: { data: dataEndChannel, projectId: this.projectId } });
    dialogRef.afterClosed().subscribe((result: Fallbacksettings) => {
      if (!result) { return; }
      this.dataEndChannelService.setBlinkerFallbackSettings(dataEndChannel.id, { anzeigeprinzip: 1, stellcode: 61, indicator: result.indicator, flashingTime: 0, delay: result.delay }).subscribe((dataEndChannel) => {
        this.dataEndChannels = this.dataEndChannels.map(x => x.id === dataEndChannel.id ? dataEndChannel : x);

        /* TODO: DataEndChannelTyp icon und beschriftung nach change nicht geupdated, prüfen!! 
        
        
        
        single response auf die put      ????????????   deType	0

        wenn alle abgefragt werden ??????????????????????      dataEndChannelType	1


        nutzt dataEndChannels Klasse backend --> da stehts wie open

        GET
  https://portal.avs-digital.de/tls2-api/RouteStation/fa9d00bf-2939-4b8b-a254-f49e9cab89e3/DataEndChannel
        */
        this.alertService.alert('Fallback Einstellungen wurden erfolgreich gespeichert', AlertTypes.info);
      });

    });
  }

  public isItDI(dataEndChannel: DataEndChannel): boolean{
    return (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.DIGITALINPUT);
  }
  public isItLED(dataEndChannel: DataEndChannel): boolean{
    return (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.LED);
  }
  public isItCluster(dataEndChannel: DataEndChannel): boolean{
    return (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.CLUSTER ||
      dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.BLINKER_CLUSTER ||
      dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.PW_CLUSTER);
  }
  public isItDO(dataEndChannel: DataEndChannel): boolean{
    return (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.DIGITALOUTPUT);
  }
  public isItBlinker(dataEndChannel: DataEndChannel): boolean{
    return (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.BLINKER);
  }
  public isItAdec(dataEndChannel: DataEndChannel): boolean {
    if (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.RADAR) {

      // Iterate through radar list
      return this.radars.some((radar) => {
        const radarType = radar.data?.radarType;
        if (!radarType || radarType.dataEndChannelRadars === undefined) {
          return false;
        }

        return radarType.dataEndChannelRadars.some((deChannelRadar) => {
          return (deChannelRadar.dataEndChannelId === dataEndChannel.id) && radarType.name === 'ADEC TDC1-PIR';
        });
      });
    }
    return false;
  }


  public whichRadarType(dataEndChannel: DataEndChannel): string {
    if (dataEndChannel.dataEndChannelType === DataEndChannelTypeEnum.RADAR) {
      // Iterate through radar list
      const radar = this.radars.find((r) => {
        const radarType = r.data?.radarType;
        if (!radarType || radarType.dataEndChannelRadars === undefined) {
          return false;
        }

        return radarType.dataEndChannelRadars.some((deChannelRadar) => {
          return deChannelRadar.dataEndChannelId === dataEndChannel.id;
        });
      });

      return radar?.data?.radarType?.name ?? '';
    }
    return '';
  }


  public isDuplicate(dataEndChannel: DataEndChannel): boolean {
    let dataEndChannels = this.dataEndChannels.filter(x => x.id !== dataEndChannel.id);
    for (let channel of dataEndChannels) {
      if (channel.de === dataEndChannel.de && channel.fg === dataEndChannel.fg && channel.id !== dataEndChannel.id) {
        return true;
      }
    }
    return false;
  }

  public onDeleteTresholds(dataEndChannel: DataEndChannel): void {
    this.modalService.openDialog('Schwellenwerte löschen?', 'Nein', 'Ja').subscribe((x) => {
      if (x === false) { return; }
      this.errorTresholdService.deleteErrorThreshold(dataEndChannel.id).subscribe((answer: any) => {
        dataEndChannel.errorThresholds = []
      });
      this.alertService.alert('Fehlergrenzen wurden erfolgreich gelöscht', AlertTypes.info);

    });
  }

  public onCreateOrEditTreshold(dataEndChannel: DataEndChannel): void {
    const dialogRef = this.dialog.open(CreateEditErrorTresholdComponent, { data: { dataEndChannel: dataEndChannel } });
    dialogRef.afterClosed().subscribe((result: ErrorThresholdParameters[]) => {
      if (!result) { return; }
      dataEndChannel.errorThresholds = result;
    });
  }

  public getChannelErrorString(dataEndChannel: DataEndChannel): string {
    let isDuplicate = this.isDuplicate(dataEndChannel);
    if (!this.dataEndChannelErrors) { return 'Unbekannt'; }
    for (let error of this.dataEndChannelErrors) {
      if (error.de === dataEndChannel.de && error.fg === dataEndChannel.fg) {
        if (error.error) {
          return 'Fehler';
        }
        else {
          return 'keine Fehler';
        }
      }
    } if (isDuplicate) {
      return 'Duplikat';
    }
    else
      return 'Unbekannt';
  }

  public getChannelStateClassString(dataEndChannel: DataEndChannel): string {
    let isDuplicate = this.isDuplicate(dataEndChannel);
    if (!this.dataEndChannelErrors) { return 'avs-state-secondary'; }
    for (let error of this.dataEndChannelErrors) {
      if (error.de === dataEndChannel.de && error.fg === dataEndChannel.fg) {
        if (error.error) {
          return 'avs-state-danger';
        } else {
          return 'avs-state-success';
        }
      }
    } if (isDuplicate) {
      return 'avs-state-error';
    } else
      return 'avs-state-secondary';
  }

  public onSurveillanceActiveChange(dataEndChannel: DataEndChannel): void {
    dataEndChannel.surveillanceActive = !dataEndChannel.surveillanceActive;
    this.dataEndChannelService.editDataEndChannel(dataEndChannel.id, dataEndChannel).subscribe(() => {
      this.alertService.alert('Überwachung wurde erfolgreich geändert', AlertTypes.info);
    });
  }

  public onLoggingActiveChange(dataEndChannel: DataEndChannel): void {
    dataEndChannel.loggingActive = !dataEndChannel.loggingActive;
    this.dataEndChannelService.editDataEndChannel(dataEndChannel.id, dataEndChannel).subscribe(() => {
      this.alertService.alert('Überwachung wurde erfolgreich geändert', AlertTypes.info);
    });
  }

  public changeOnLoggingActive() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data:
      {
        dataEndChannels: this.dataEndChannels,
        title: 'Protokollierung?',
        message: 'Protokollierung für alle DEs aktivieren?',
        confirmButtonText: 'Anschalten',
        cancelButtonText: 'Ausschalten',
      }
    })
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result !== undefined) {
        this.dataEndChannelService.updateDataEndChannelsLoggingByGuid(this.routeStationGuid!, result).subscribe(() => {
          this.alertService.alert('Protokollierung wurde erfolgreich geändert', AlertTypes.info);
          this.dataEndChannelRefresh.emit(true);
        }
        )
      }
    })

  }

  public changeOnSurveillance() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data:
      {
        dataEndChannels: this.dataEndChannels,
        title: 'Überwachung?',
        message: 'Überwachung für alle DEs aktivieren?',
        confirmButtonText: 'Anschalten',
        cancelButtonText: 'Ausschalten',
      }
    })
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result !== undefined) {
        this.dataEndChannelService.updateDataEndChannelsSurveillanceByGuid(this.routeStationGuid!, result).subscribe(() => {
          this.alertService.alert('Überwachung wurde erfolgreich geändert', AlertTypes.info);
          this.dataEndChannelRefresh.emit(true);
        }
        )
      }
    })

  }
}
